import { useEffect, useState, type ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';

import { LoadFailure } from '../../components/LoadFailure';
import { TableLoading } from '../../components/TableLoading';
import { useAuth } from '../../core/authContext';
import { authSnapshot } from '../ProgressReports/students';
import { Card, fileLabel, IMPLEMENTATION_ROUTE, outlinedButton, PeriodBar, primaryButton, Shell, Stats, Tag, useReportQuery } from '../ImplementationReport';
import type { Granularity } from '../ImplementationReport/api';
import { localToday, shortLabel } from '../ImplementationReport/period';
import { csvText, downloadCsv } from '../ImplementationReport/rows';
import { USAGE } from '../ImplementationReport/tokens';
import { fetchAssignmentLibrary, viewerZone, type AssignmentLibraryResponse, type LibraryAssignment, type LibraryFile, type LibraryQuery, type LibraryReport, type LibrarySource } from './api';
import { applyFilters, CSV_COLUMNS, csvRow, dayLabel, domainOptions, emptyFilters, entryCsv, gradeLabel, isImage, picksKey, promptBlocks, promptOf, readPicks, schoolNames, schoolOptions, tagsOf, teacherName, teacherOptions, typeChips, writePicks, type Filters, type PromptBlock } from './rows';
import { LIBRARY } from './tokens';

export const LIBRARY_ROUTE = '/reports/library';
export const LIBRARY_PRINT_ROUTE = '/reports/library/print';

export const UNAVAILABLE_TEXT = 'Assignment library data is not available yet.';

type Loaded = { kind: 'loading' } | { kind: 'error' } | { kind: 'ready'; data: AssignmentLibraryResponse };

export type Ready = { data: AssignmentLibraryResponse; report: LibraryReport; granularity: Granularity; periodShort: string };

export function useLibrary(source: LibrarySource, query: LibraryQuery, enabled: boolean): Loaded {
  const { granularity, start, today, tz, assignment } = query;
  const key = `${granularity}|${start ?? ''}|${today ?? ''}|${tz ?? ''}|${assignment ?? ''}`;
  const [settled, setSettled] = useState<{ key: string; loaded: Loaded } | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    authSnapshot()
      .then((auth) => (active ? source(auth, { granularity, start, today, tz, assignment }) : null))
      .then((data) => {
        if (active && data) setSettled({ key, loaded: { kind: 'ready', data } });
      })
      .catch(() => {
        if (active) setSettled({ key, loaded: { kind: 'error' } });
      });
    return () => {
      active = false;
    };
  }, [key, granularity, start, today, tz, assignment, source, enabled]);
  return settled?.key === key ? settled.loaded : { kind: 'loading' };
}

export function readyOf(data: AssignmentLibraryResponse): Ready | null {
  if (!data.available || !data.report || !data.period || !data.granularity) return null;
  return { data, report: data.report, granularity: data.granularity, periodShort: shortLabel(data.granularity, data.period.start, data.period.fullEnd ?? data.period.end) };
}

const font = { fontFamily: USAGE.font };
const body = { ...font, fontSize: 16, fontWeight: 400, lineHeight: '24px', color: USAGE.ink };
const muted = { ...body, color: USAGE.muted };
const smallLabel = { ...font, fontSize: 12, fontWeight: 700, lineHeight: '16px', letterSpacing: '1px', textTransform: 'uppercase' as const, color: USAGE.muted };
const title = { ...font, fontSize: USAGE.title.size, fontWeight: USAGE.title.weight, lineHeight: USAGE.title.lineHeight, letterSpacing: USAGE.title.letterSpacing, color: USAGE.ink };
const promptText = { ...font, fontSize: LIBRARY.prompt.size, fontWeight: LIBRARY.prompt.weight, lineHeight: LIBRARY.prompt.lineHeight, color: USAGE.ink };
const teacherLink = { ...font, color: USAGE.primary, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } };

export function PromptPicture({ file, alt, width }: { file: LibraryFile | undefined; alt: string; width: number }) {
  const [hidden, setHidden] = useState(false);
  if (!file || !isImage(file) || hidden) return null;
  return <Box component="img" src={file.url} alt={alt} onError={() => setHidden(true)} sx={{ flex: `0 0 ${width}px`, width, maxHeight: Math.round(width * 2 / 3), objectFit: 'cover', borderRadius: '8px', bgcolor: USAGE.surface }} />;
}

function Tags({ a, ready }: { a: LibraryAssignment; ready: Ready }) {
  return (
    <Box sx={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
      {tagsOf(a, ready.report.caseloads).map((tag, i) => <Tag key={tag} text={tag} indigo={i === 0} testId="lib-tag" />)}
    </Box>
  );
}

function Chip({ chip, on, onClick }: { chip: { label: string; count: number }; on: boolean; onClick: () => void }) {
  return (
    <Box component="button" type="button" data-testid="lib-chip" aria-pressed={on} aria-label={`${chip.label} ${chip.count}`} onClick={onClick}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: '8px', height: LIBRARY.chipHeight, px: '16px', boxSizing: 'border-box', border: `1px solid ${on ? USAGE.primary : USAGE.divider}`, borderRadius: '9999px', bgcolor: on ? USAGE.primary : USAGE.cardBg, color: on ? USAGE.primaryContrast : USAGE.ink, ...font, fontSize: 16, fontWeight: 600, lineHeight: '24px', cursor: 'pointer', '&:hover': { bgcolor: on ? USAGE.primaryDark : USAGE.hoverBg } }}>
      {chip.label}
      <Box component="small" sx={{ ...font, fontSize: 16, fontWeight: 600, lineHeight: '24px', color: on ? USAGE.primaryContrast : USAGE.muted, opacity: on ? 0.8 : 1 }}>{chip.count}</Box>
    </Box>
  );
}

function FilterOption({ testId, label, count, on, onClick }: { testId: string; label: string; count: number; on: boolean; onClick: () => void }) {
  return (
    <Box component="button" type="button" role="checkbox" aria-checked={on} data-testid={testId} onClick={onClick}
      sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', py: '4px', border: 'none', bgcolor: 'transparent', textAlign: 'left', cursor: 'pointer', ...body }}>
      <Box component="span" aria-hidden sx={{ width: 16, height: 16, flexShrink: 0, boxSizing: 'border-box', border: `2px solid ${on ? USAGE.primary : USAGE.muted}`, borderRadius: '4px', bgcolor: on ? USAGE.primary : 'transparent' }} />
      <Box component="span" sx={{ flex: '1 1 0', minWidth: 0 }}>{label}</Box>
      <Box component="span" sx={{ color: USAGE.muted }}>{count}</Box>
    </Box>
  );
}

function FilterGroup({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <Box>
      <Typography component="h3" sx={{ ...smallLabel, mb: '4px' }}>{heading}</Typography>
      {children}
    </Box>
  );
}

const moreButton = { border: 'none', bgcolor: 'transparent', p: 0, ...font, fontSize: 16, fontWeight: 700, lineHeight: '24px', color: USAGE.primary, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } };

function FiltersPanel({ ready, filters, onChange }: { ready: Ready; filters: Filters; onChange: (next: Filters) => void }) {
  const [allTeachers, setAllTeachers] = useState(false);
  const teachers = teacherOptions(ready.report.assignments);
  const shown = allTeachers ? teachers : teachers.slice(0, LIBRARY.teachersShown);
  const toggle = <T,>(set: Set<T>, value: T): Set<T> => {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  };
  return (
    <Box component="aside" data-testid="lib-filters" sx={{ flex: `0 0 ${LIBRARY.filtersWidth}px`, boxSizing: 'border-box', p: '24px', bgcolor: USAGE.cardBg, border: `1px solid ${USAGE.divider}`, borderRadius: `${USAGE.card.radius}px`, display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <FilterGroup heading="School">
        {schoolOptions(ready.report).map((s) => <FilterOption key={s.id} testId="lib-filter-school" label={s.name} count={s.count} on={!filters.schoolsOff.has(s.id)} onClick={() => onChange({ ...filters, schoolsOff: toggle(filters.schoolsOff, s.id) })} />)}
      </FilterGroup>
      <FilterGroup heading="Domain">
        {domainOptions(ready.report.assignments).map((d) => <FilterOption key={d.domain} testId="lib-filter-domain" label={d.domain} count={d.count} on={!filters.domainsOff.has(d.domain)} onClick={() => onChange({ ...filters, domainsOff: toggle(filters.domainsOff, d.domain) })} />)}
      </FilterGroup>
      <FilterGroup heading="Teacher">
        {shown.map((t) => <FilterOption key={t.userId} testId="lib-filter-teacher" label={t.name} count={t.count} on={filters.teachers.has(t.userId)} onClick={() => onChange({ ...filters, teachers: toggle(filters.teachers, t.userId) })} />)}
        {teachers.length > LIBRARY.teachersShown && (
          <Box component="button" type="button" onClick={() => setAllTeachers((v) => !v)} sx={{ ...moreButton, mt: '12px' }}>{allTeachers ? 'Show fewer' : `Show all ${teachers.length} teachers`}</Box>
        )}
      </FilterGroup>
    </Box>
  );
}

function Tick({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <Box component="button" type="button" role="checkbox" aria-checked={on} aria-label="Select for printing" onClick={onToggle}
      sx={{ flex: `0 0 ${LIBRARY.tick.size}px`, width: LIBRARY.tick.size, height: LIBRARY.tick.size, p: 0, boxSizing: 'border-box', border: `2px solid ${on ? USAGE.primary : USAGE.muted}`, borderRadius: `${LIBRARY.tick.radius}px`, bgcolor: on ? USAGE.primary : USAGE.cardBg, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { borderColor: USAGE.primary } }}>
      {on && <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true"><path d="M3 7.5 6 10.5 11 4" fill="none" stroke={USAGE.primaryContrast} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </Box>
  );
}

function AssignmentCard({ a, ready, search, picked, onToggle }: { a: LibraryAssignment; ready: Ready; search: string; picked: boolean; onToggle: () => void }) {
  const facts = [schoolNames(a, ready.report.schools), gradeLabel(a.grades), dayLabel(a.startAt)].filter((f) => f !== '').join(' · ');
  const school = a.schoolIds[0] ?? '';
  return (
    <Box data-testid="lib-card" sx={{ display: 'flex', gap: `${LIBRARY.card.gap}px`, p: LIBRARY.card.padding, bgcolor: USAGE.cardBg, border: `1px solid ${picked ? USAGE.primary : USAGE.divider}`, borderRadius: `${LIBRARY.card.radius}px` }}>
      <Tick on={picked} onToggle={onToggle} />
      <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Tags a={a} ready={ready} />
        <Box component={Link} to={`${LIBRARY_ROUTE}/${a.id}${search}`} data-testid="lib-prompt" sx={{ ...promptText, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>{promptOf(a) || a.title}</Box>
        <Typography data-testid="lib-by" sx={muted}>
          <Box component={Link} to={`${IMPLEMENTATION_ROUTE}/school/${school}/teacher/${a.teacherUserId}${search}`} sx={teacherLink}>{teacherName(a)}</Box>
          {` ${facts}`}
        </Typography>
      </Box>
      <Box data-testid="lib-nums" sx={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', textAlign: 'right' }}>
        <Box><Box component="b" sx={{ display: 'block', ...font, fontSize: 20, fontWeight: 800, lineHeight: '28px', color: USAGE.ink }}>{`${a.submitted} / ${a.assigned}`}</Box><Box component="small" sx={{ ...font, fontSize: 12, lineHeight: '16px', color: USAGE.muted }}>submitted</Box></Box>
        <Box><Box component="b" sx={{ display: 'block', ...font, fontSize: 20, fontWeight: 800, lineHeight: '28px', color: USAGE.ink }}>{a.scored}</Box><Box component="small" sx={{ ...font, fontSize: 12, lineHeight: '16px', color: USAGE.muted }}>scored</Box></Box>
      </Box>
    </Box>
  );
}

export function Unavailable() {
  return <Card><Typography sx={{ ...body, fontWeight: 700 }}>{UNAVAILABLE_TEXT}</Typography></Card>;
}

export function LoadState({ loaded }: { loaded: Loaded }) {
  if (loaded.kind === 'loading') return <Card><TableLoading tokens={USAGE} rows={4} /></Card>;
  if (loaded.kind === 'error') return <Card><LoadFailure tokens={USAGE} /></Card>;
  return readyOf(loaded.data) ? null : <Unavailable />;
}

export function LibraryPage({
  source = fetchAssignmentLibrary,
  onExport = downloadCsv,
  today = localToday(),
  zone = viewerZone(),
}: {
  source?: LibrarySource;
  onExport?: (name: string, text: string) => void;
  today?: string;
  zone?: string;
}) {
  const { isDistrictAdmin, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const query = useReportQuery();
  const allowed = isDistrictAdmin || isSuperAdmin;
  const loaded = useLibrary(source, { granularity: query.granularity, start: query.start, today, tz: zone }, allowed);
  const [filters, setFilters] = useState<Filters>(emptyFilters);
  const [more, setMore] = useState(false);
  const key = picksKey(query.granularity, query.start);
  const [picked, setPicked] = useState<{ key: string; ids: string[] }>(() => ({ key, ids: readPicks(key) }));
  if (!allowed) return <Navigate to="/reports" replace />;

  const ready = loaded.kind === 'ready' ? readyOf(loaded.data) : null;
  const picks = picked.key === key ? picked.ids : readPicks(key);
  const setPicks = (ids: string[]) => {
    writePicks(key, ids);
    setPicked({ key, ids });
  };
  const inReport = ready ? picks.filter((id) => ready.report.assignments.some((a) => a.id === id)) : picks;
  const setQuery = (next: { granularity: Granularity; start?: string }) => {
    setPicks([]);
    setFilters(emptyFilters());
    setMore(false);
    const params = new URLSearchParams({ granularity: next.granularity });
    if (next.start) params.set('start', next.start);
    navigate({ search: `?${params.toString()}` }, { replace: true });
  };
  const changeFilters = (next: Filters) => {
    setFilters(next);
    setMore(false);
  };
  const list = ready ? applyFilters(ready.report.assignments, filters) : [];
  const shown = more ? list : list.slice(0, LIBRARY.listPage);
  const exportCsv = () => {
    if (!ready) return;
    onExport(`assignment-library-${fileLabel(ready.periodShort)}-${ready.data.period!.end.slice(0, 4)}.csv`, csvText(CSV_COLUMNS, list.map((a) => csvRow(a, ready.report))));
  };
  const right = (
    <>
      {ready && <Box component="button" type="button" onClick={exportCsv} sx={outlinedButton}>Export</Box>}
      {inReport.length > 0 ? (
        <Box component={Link} to={LIBRARY_PRINT_ROUTE + query.search} sx={primaryButton}>{`Print ${inReport.length} selected`}</Box>
      ) : (
        <Box component="button" type="button" disabled sx={{ ...primaryButton, '&:disabled': { opacity: 0.5, cursor: 'default' } }}>Print selected</Box>
      )}
    </>
  );

  return (
    <Shell crumbs={[{ label: 'Reports', to: '/reports' }, { label: 'Assignment library' }]}>
      <Typography component="h1" sx={title}>Assignment library</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <PeriodBar granularity={query.granularity} start={query.start} today={today} onChange={setQuery} right={right} />
        {ready && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {typeChips(ready.report.assignments, filters).map((chip) => <Chip key={chip.key} chip={chip} on={filters.type === chip.key} onClick={() => changeFilters({ ...filters, type: chip.key })} />)}
          </Box>
        )}
      </Box>
      <LoadState loaded={loaded} />
      {ready && (
        <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <FiltersPanel ready={ready} filters={filters} onChange={changeFilters} />
          <Box sx={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {inReport.length > 0 && (
              <Box data-testid="lib-selbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: LIBRARY.selbar.height, px: '24px', boxSizing: 'border-box', borderRadius: '8px', bgcolor: LIBRARY.selbar.bg, color: USAGE.primary, ...font, fontSize: 16, fontWeight: 700, lineHeight: '24px' }}>
                <Box component="b">{`${inReport.length} selected`}</Box>
                <Box component="button" type="button" onClick={() => setPicks([])} sx={{ ...moreButton, color: USAGE.primary }}>Clear</Box>
              </Box>
            )}
            {list.length === 0 && <Card><Typography data-testid="lib-empty" sx={{ ...muted, py: '16px', textAlign: 'center' }}>No assignments match these filters.</Typography></Card>}
            {shown.map((a) => (
              <AssignmentCard key={a.id} a={a} ready={ready} search={query.search} picked={picks.includes(a.id)} onToggle={() => setPicks(picks.includes(a.id) ? picks.filter((id) => id !== a.id) : [...picks, a.id])} />
            ))}
            {list.length > LIBRARY.listPage && (
              <Box><Box component="button" type="button" onClick={() => setMore((v) => !v)} sx={moreButton}>{more ? 'Show fewer' : `Show ${list.length - LIBRARY.listPage} more`}</Box></Box>
            )}
          </Box>
        </Box>
      )}
    </Shell>
  );
}

function PromptBlockView({ block }: { block: PromptBlock }) {
  return (
    <Box data-testid="lib-assignment-prompt" sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <Typography component="small" sx={smallLabel}>{block.label}</Typography>
      {block.text !== '' && <Typography sx={{ ...promptText, whiteSpace: 'pre-wrap' }}>{block.text}</Typography>}
      <PromptPicture file={block.file} alt={`${block.label} picture`} width={240} />
    </Box>
  );
}

export function AssignmentPage({
  source = fetchAssignmentLibrary,
  onExport = downloadCsv,
  today = localToday(),
  zone = viewerZone(),
}: {
  source?: LibrarySource;
  onExport?: (name: string, text: string) => void;
  today?: string;
  zone?: string;
}) {
  const { isDistrictAdmin, isSuperAdmin } = useAuth();
  const { assignmentId = '' } = useParams();
  const query = useReportQuery();
  const allowed = isDistrictAdmin || isSuperAdmin;
  const loaded = useLibrary(source, { granularity: query.granularity, start: query.start, today, tz: zone, assignment: assignmentId }, allowed);
  if (!allowed) return <Navigate to="/reports" replace />;
  const ready = loaded.kind === 'ready' ? readyOf(loaded.data) : null;
  const a = ready?.report.assignments.find((entry) => entry.id === assignmentId);
  if (ready && !a) return <Navigate to={LIBRARY_ROUTE + query.search} replace />;
  const crumbs = [{ label: 'Reports', to: '/reports' }, { label: 'Assignment library', to: LIBRARY_ROUTE + query.search }, { label: a?.title ?? 'Assignment' }];
  return (
    <Shell crumbs={crumbs}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
        <Box>
          <Typography component="h1" sx={title}>{a?.title ?? 'Assignment'}</Typography>
          {a && ready && (
            <Typography data-testid="lib-sub" sx={{ ...muted, mt: '4px' }}>
              {[teacherName(a), schoolNames(a, ready.report.schools), gradeLabel(a.grades), `Assigned ${dayLabel(a.startAt)}`, `Due ${dayLabel(a.closeAt)}`].filter((f) => f !== '').join(' · ')}
            </Typography>
          )}
        </Box>
        {a && ready && <Box component="button" type="button" onClick={() => onExport(`assignment-${fileLabel(a.title)}.csv`, entryCsv(a, ready.report))} sx={outlinedButton}>Export</Box>}
      </Box>
      <LoadState loaded={loaded} />
      {a && ready && (
        <>
          <Card>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Tags a={a} ready={ready} />
              {promptBlocks(a).map((block) => <PromptBlockView key={block.label} block={block} />)}
            </Box>
          </Card>
          <Stats stats={[
            { label: 'Assigned', value: String(a.assigned), sub: '' },
            { label: 'Submitted', value: String(a.submitted), sub: '' },
            { label: 'Scored', value: String(a.scored), sub: '' },
            { label: 'Not started', value: String(a.assigned - a.submitted), sub: '' },
          ]} />
        </>
      )}
    </Shell>
  );
}
